import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'

import QMenuStore from './modules/QMenuStore'
import QVocabularyStore from './modules/QVocabularyStore'

import UserSessionStore from './UserSession';

import Site from '@/api/Site'
import QCentro from '@/api/QCentro';
import QUnit from '@/api/QUnit'
import User from '@/api/User'
import Standard from '@/api/Standard'
import Process from '@/api/Process'
import QMenuItem from '@/api/QMenuItem'
import QVocabularyItem from '@/api/QVocabularyItem'
import QDirectory from '@/api/QDirectory'
import QReportsItem from '@/api/QReportsItem'
import ExplorerFilters from '@/utils/ExplorerFilters';
import { QFilters, get_qfilter } from '@/qunit/filters';
import QUnitCollection from '@/qunit/QUnitCollection';
import BusinessProcessCollection from '@/qunit/BusinessProcessCollection';
import QToken from '@/api/QToken.js';

Vue.use(Vuex);

const getDefaultState = () => ({
  allProcesses: [],
  processes: {}, // {'proc_id': {process}}
  currentTheme: '',
  current_parent_process: '',
  current_unit: {},
  context_title: 'Processes',
  filterFormData: [],
  isLoading: true,
  menus: [],
  alert_message: {}, // {type: (error,warning,success), text: 'Error message', timeout: -1}
  processID: '',  // Deprecated use filterProcesses instead
  searchText: '',
  globalSearchText: '',
  rootSite: '',
  sites: [], // All sites
  sites_map: {}, // {id: site_obj}
  current_site: {}, // site_obj
  unitColumns: [],
  qunits: new QUnitCollection({}),
  unitsItemsPerPage: 10,
  users: [],
  users_map: {},
  notifications: new QUnitCollection({}),
  notifications_count: 0,
  filterDates: [],
  calendar_units: [],
  qdirectory_centro: {},      // {centro_id: {dir_id: {directory props}}}
  qdirectory: {},             // {dir_id: {directory props}}
  qdirectory_centro_list: {}, // {centro_id: [qdirectory: {sub_qdirectory}]}
  activeDirectory: {},        // Active directory
  filterProcesses: [],        // Selected processes ids to filter QUnits
  explorer_filters: new QFilters(),
  todolist_filters: new QFilters(),
  bpmap: {},
  loading_lock: new Set(), // Items that require the app to freeze untill loaded
  loading_lock_count: 0,   // =loading_lock.size // Vue Reactivity. Must be manually updated
  is_mobile: false,
  disabled_components: {},
  centros: {}, // {uuid: {id: uuid, name: 'a name'...}}
  site_centros: {}, // {site_id: {centro_data}}
});

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function loading_lock(state, name) {
  // Call me to lock state.loading_lock
  // name is the calling function name
  state.loading_lock.add(name);
  state.loading_lock_count = state.loading_lock.size; 
  console.log('LOADING LOCK: ', state.loading_lock);
}

function loading_unlock(state, name) {
  // Call me to unlock state.loading_lock
  // name is the calling function name
  state.loading_lock.delete(name);
  state.loading_lock_count = state.loading_lock.size; 
  console.log('LOADING UNLOCK: ', state.loading_lock); // DEBUG
}

function loading_flush(state) {
  state.loading_lock.clear();
  state.loading_lock_count = 0;
}

// initial state
const state = getDefaultState();

const getters = {
  isUser    : state => (state.UserSession.user && state.UserSession.user.id > 0),
  isManager : state => (state.UserSession.user && state.UserSession.user.isManager),
  isAdmin   : state => (state.UserSession.user && state.UserSession.user.isAdmin),
  site_data : state => state.UserSession.site_data,
  sitePath  : state => (state.UserSession.site_data||{}).path || null,
  siteRoot  : state => state.UserSession.root_site,
  site_id   : state => (state.UserSession.site_data||{}).id || null,
  centro_id : state => (state.UserSession.centro || null),
  centro_data : state => (state.centros[state.UserSession.centro||-1] || {}),
  user: state => (state.UserSession.user || { id: null }),
  current_unit_uid: state => (state.current_unit || {uid: null}).uid,
  is_loading_locked: state => (state.loading_lock.size > 0),
};

const actions = {
  async GET_SITES({ commit, dispatch }, site_id) {
    loading_lock(state, 'GET_SITES');
    const us_update = dispatch("UserSession/update_site_data");
    try {
      const data = await Site.getAll();
      commit('SET_SITES', data['results'] || data);
    } catch (error) {
      //commit('SET_MESSAGE', error);
    }
    await us_update;
    loading_unlock(state, 'GET_SITES');
  },

  async GET_CENTROS({ commit, dispatch, state }, site_id) {
    loading_lock(state, 'GET_CENTROS');
    try {
      const data = await QCentro.list(getters.siteRoot(state));
      commit('SET_CENTROS', data['results'] || data);
    } catch (error) {
      //commit('SET_MESSAGE', error);
      console.error(error);
    }
    loading_unlock(state, 'GET_CENTROS');
  },

  async GOTO_CENTRO({ commit, dispatch, state }, {centro_id, context}) {
    const previous = getters.centro_data(state);
    const centro = state.centros[centro_id];
    if (!centro || !state.sites_map[centro.site])
      throw `Can't browse to centro ${centro_id} because user has no access to it`;

    dispatch("UserSession/set_centro", centro_id);

    if (!previous || previous.site != centro.site) {
      const new_site = state.sites_map[centro.site]; 
      dispatch("UserSession/set_site", new_site);
      dispatch("REFRESH_STATE", context);
    } else {
      // TODO: LOAD MENU
    }
  },

  UPDATE_CENTRO_ACTION({ commit, dispatch }, centro) {
    commit('UPDATE_CENTRO', centro);
  },

  async GET_BUSINESS_PROCESS_MAP({ commit }, centro_id) {
    loading_lock(state, 'GET_BUSINESS_PROCESS_MAP');
    const bpmap = await BusinessProcessCollection.fetch(centro_id);
    commit('SET_BPMAP', bpmap);
    loading_unlock(state, 'GET_BUSINESS_PROCESS_MAP');
  },

  CHANGE_THEME({ commit }, theme) {
    commit('SET_CURRENT_THEME', theme);
  },

  LOAD_PROCESSES({ commit }, site) {
    commit('SET_LOADING', true)
    return Process.getTree(site)
      .then(data => {
        commit('SET_PROCESSES', data)
      })
      //.catch(error => commit('SET_MESSAGE', error))
  },

  LOAD_MENU_ITEMS({ commit }, centro_id) {
    loading_lock(state, 'LOAD_MENU_ITEMS');
    return QMenuItem
      .getQMenuItemsFull(centro_id)
      .then(data => {
        commit('SET_MENU_ITEMS', data)
        loading_unlock(state, 'LOAD_MENU_ITEMS');
      })
      .catch(error => {
        //commit('SET_MESSAGE', error);
        loading_unlock(state, 'LOAD_MENU_ITEMS');
      });
  },

  LOAD_VOCABULARY_ITEMS({ commit }, centro_id) {
    commit('SET_LOADING', true);
    return QVocabularyItem.getQVocabularyItems(centro_id)
      .then(data => {
        commit('SET_VOCABULARY_ITEMS', data);
      });
      //.catch(error => commit('SET_MESSAGE', error))
  },

  SET_EXPLORER_FILTERS({ state }, filters) {
    const sync_filters = [
      'pk',
      'process_id',
      'processes_ids',
      'centro_list',
      'units_per_page',
      // 'search', // @Joan decision 2023-09-25
      // 'date_range', // 2023-10-23 → Inconsistent date_fields between BPs
      // 'filter_dates', @Joan decision 2023-09-25
    ];

    const ef = new QFilters();

    for (const key of sync_filters)
      if (filters[key] !== undefined)
        ef[key] = filters[key];

    state.explorer_filters = ef;
  },

  SET_TODOLIST_FILTERS({ state }, filters) {
    const sync_filters = [
      'pk',
      'process_id',
      'processes_ids',
      'units_per_page',
      'search',
      'date_range',
      'filter_dates',
    ];

    const ef = new QFilters();

    for (const key of sync_filters)
      if (filters[key] !== undefined)
        ef[key] = filters[key];

    state.todolist_filters = ef;
  },

  EXPLORE_UNITS(
    { commit },
    {
      site,
      bp_reference,
      process_id,
      search_text,
      filters_form,
      units_page,
      units_sort_by = 'current_workflow_limit',
      units_sort_desc = false,
      pk = '',
      filter_dates = [],
      filter_directory = [],
      exclude_states = false,
      processes_ids = []
    },
  ) {
    /****************************************************************************/
    /**                               H A C K E D                              **/
    /****************************************************************************/

    return; //////////////////////////////////////////////////////////////////////

    /**
     * Since 2023-07-14, this is no longer the method to explore QUnits,
     */

    /****************************************************************************/

    // Generate ExplorerFilters.
    //   Ideally, this should be done by components, and we should only need to
    //   retrieve the "filters" parameter. But for legacy reasons, we have this.
    const filters = new ExplorerFilters({
      site: site,
      business_process: bp_reference,
      units_per_page: state.unitsItemsPerPage,
      page: units_page,
      sort_by: units_sort_by,
      sort_desc: units_sort_desc,
      exclude_states: exclude_states,
    });
    if (pk)
      filters.pk = pk;
    if (filter_dates && filter_dates.length > 0)
      filters.date_range = filter_dates;
    if (filter_dates && filter_dates.length > 0)
      filters.filter_dates = filter_dates;
    if (filter_directory && filter_directory.length > 0)
      filters.directories = filter_directory;
    if (search_text)
      filters.search = search_text;
    if (process_id)
      filters.process_id = process_id;
    if (processes_ids && processes_ids.length > 0)
      filters.processes_ids = processes_ids
    if (filters_form) {
      filters.advanced_filters = {};
      for (let [name, value] of filters_form.entries()){
        filters.advanced_filters[name] = value;
      }
    }

    // if (processes_ids && processes_ids.length >0) {
    //   filters.processes_ids = processes_ids
      
    //   console.log(processes_ids)
    // }

    // IDK Why this is here, but I'm afraid to touch it:
    commit('RESET_QUNITS_DATA')
    if (state.isLoading) {
      console.log('waiting')
      sleep(500)
    }
    commit('SET_LOADING', true)

    // Query Qunits
    return QUnitCollection
      .explore(filters)
      .then((qunits) => {
        commit('SET_QUNITS_DATA', qunits);
        commit('SET_CONTEXT_TITLE', qunits.bp_name);
        // commit('SET_FILTER_PROCESSES', filters.processes_ids);
        console.log("EXPLORE_UNITS page_number", qunits.page);
        commit('SET_LOADING', false);
      })
      .catch((err) => {
        //commit('SET_MESSAGE', err);
      });
  },

  EXPLORE_UNITS_CALENDAR(
    { commit },
    explorer_filters) {
    // commit('SET_LOADING', true)
    commit('RESET_CALENDAR_UNITS_DATA')
    return QUnit.getExplorerCalendar(
      explorer_filters
    ).then(data => {
      commit('SET_CALENDAR_UNITS_DATA', data)
    })
      .catch(
      error => {
        //commit('SET_MESSAGE', error)
      },
    )
  },

  EXPLORE_UNITS_TODOLIST(
      { commit, state },
      {
        site,
        user_id,
        process_id,
        search_text,
        units_page,
        units_sort_by = 'current_workflow_limit',
        units_sort_desc = false,
        pk = '',
        filters_form = null,
        filter_dates = [],
        exclude_states = false,
        processes_ids = []
      },
  ) {
    // Generate ExplorerFilters.
    //   Ideally, this should be done by components, and we should only need to
    //   retrieve the "filters" parameter. But for legacy reasons, we have this.
    const filters = new ExplorerFilters({
      site: site,
      units_per_page: state.unitsItemsPerPage,
      page: units_page,
      sort_by: units_sort_by,
      sort_desc: units_sort_desc,
      workflow_responsible: user_id,
      exclude_states: exclude_states,
      processes_ids: processes_ids,
    });

    if (pk)
      filters.pk = pk;
    if (filter_dates && filter_dates.length > 0)
      filters.date_range = filter_dates;
    if (filter_dates && filter_dates.length > 0)
      filters.filter_dates = filter_dates;
    if (search_text)
      filters.search = search_text;
    if (process_id)
      filters.process_id = process_id;


    if (filters_form) {
      filters.advanced_filters = {};
      for (let [name, value] of filters_form.entries())
        filters.advanced_filters[name] = value;
    }

    // IDK Why this is here, but I'm afraid to touch it:
    commit('RESET_QUNITS_DATA')
    if (state.isLoading) {
      console.log('waiting')
      sleep(500)
    }
    commit('SET_LOADING', true)

    // Query Qunits
    return QUnitCollection
      .todoList(filters)
      .then((qunits) => {
        commit('SET_QUNITS_DATA', qunits);
        console.log("EXPLORE_UNITS page_number", qunits.page);
      commit('SET_LOADING', false);
    })
    .catch((err) => {
      //commit('SET_MESSAGE', err);
    });
},

  UPDATE_UNITS_TABLE({ commit, state }, { new_units }) {
    if (state.isLoading) {
      console.log('waiting')
      sleep(500)
    }
    commit('SET_UNITS', new_units)
    commit('SET_LOADING', false)        
  },

  UPDATE_QUNIT_TABLE({ commit, state }, { uid, value }) {
    // uid → "QUnit#ACCIONUNIVERSALDEFAULT#11#54c85ddc-0d7c-495e-8fef-cc23e66de2ec"
    // value → { backend returned QUnit data }
    const res = state.qunits.replace_unit(uid, value);
    commit('SET_QUNITS', res);
    // commit('SET_LOADING', false)
  },

  GET_USERS({ commit }, site) {
    return User
      .getAll(site)
      .then(data => {
        commit('SET_USERS', data['results'])
      })
      .catch(error => console.log('ERROR GETTING USERS ' + error))
  },

  RESET_STATE({ commit }) {
    commit('RESET_STATE');
  },

  DEFINE_ROOT_SITE({ commit }, site) {
    commit('SET_ROOT_SITE', site);
  },

  REFRESH_STATE({ commit, state, dispatch, getters }, context) {
    loading_flush(state);
    loading_lock(state, 'REFRESH_STATE');

    if (
      context.$auth.isAuthenticated()
      && state.UserSession.user
      && !getters.isUser
      && state.UserSession.token
    ) {
      (async () => {
        try {
          const tkdata = QToken.get(state.UserSession.token);
          if (!tkdata.key)
            throw 'No token';
          commit('UserSession/set_token', tkdata.key);

        } catch (err) {
          commit('UserSession/purge_session');
          context.$auth.logout();
          context.$router.push('login');
        }
      })();
    }

    if (state.UserSession.site && state.UserSession.user) {
      dispatch('GET_SITES', state.UserSession.root_site);
      dispatch('GET_CENTROS', state.UserSession.root_site);
      dispatch('GET_BUSINESS_PROCESS_MAP', state.UserSession.centro);
      dispatch('LOAD_PROCESSES', state.UserSession.site_data.path);
      dispatch('LOAD_MENU_ITEMS', state.UserSession.centro);
      dispatch(
        'LOAD_VOCABULARY_ITEMS',
        state.UserSession.centro,
      );
      dispatch(
        'LOAD_DIRECTORY_ITEMS',
        state.UserSession.centro,
      );
    }

    loading_unlock(state, 'REFRESH_STATE');
  },

  LOAD_USER_NOTIFICATIONS({ commit, state }, context){
    if (context.$auth.isAuthenticated() && state.UserSession.user) {
      const filters = new QFilters({
        site: state.UserSession.site_data.path,
        units_per_page: state.unitsItemsPerPage,
        page: 1,
        sort_by: "fecha_creacion",
        sort_desc: true,
      });
      QUnit.getUserNotifications(
        filters
      )
      .then(data => {
        commit('SET_CURRENT_USER_NOTIFICATIONS', data);
      })
      .catch(error => {
        //commit('SET_MESSAGE', error)
        // commit('SET_LOADING', false)
      });
    }
  },

  LOAD_USER_NOTIFICATIONS_COUNT({ commit, state }, context){
    if (context.$auth.isAuthenticated() && state.UserSession.user) {
      QUnit.getUserNotificationsCount(
        state.UserSession.centro,
        'NOTIF_DEFAULT',
        state.UserSession.user.id
      )
      .then(data => {
        commit('SET_CURRENT_USER_NOTIFICATIONS_COUNT', data)
      })
      .catch(error => {
        //commit('SET_MESSAGE', error)
        // commit('SET_LOADING', false)
      });
    }
  },

  async LOAD_DIRECTORY_ITEMS({ commit }, centro_id) {
    commit('SET_LOADING', true);
    const data = await QDirectory.list(centro_id);
    commit('SET_DIRECTORY_ITEMS', data.data);
    commit('SET_LOADING', false);
  },

  async GET_REPORT_LIST_TEMPLATES({ commit, state, getters }, bp_name) {
    try {
      const data = await QReportsItem.listListTemplates(getters.sitePath, bp_name);
      commit('SET_REPORT_LIST_TEMPLATES', data.data);
    } catch (error) {
      commit('SET_REPORT_LIST_TEMPLATES', {});
      console.warn(error);
    }
  },
};

const mutations = {
  RESET_STATE(state) {
    const mobile_state = state.is_mobile;
    Object.assign(state, getDefaultState());
    this.commit('SET_IS_MOBILE', mobile_state);
  },

  SET_BPMAP(state, bpmap) {
    state.bpmap = bpmap;
  },

  SET_GLOBAL_SEARCH_TEXT(state, text) {
    state.globalSearchText = text;
  },

  SET_CURRENT_SITE(state, sitePath) {
    state.UserSession.site = sitePath;
  },

  SET_ROOT_SITE(state, sitePath) {
    state.rootSite = sitePath;
  },

  SET_SITES(state, sites) {
    const sites_map = {};
    for (const site of sites) {
      sites_map[site.id] = site;
    }
    state.sites = sites;
    state.sites_map = sites_map;
    const site = sites_map[state.UserSession.site_data.id];

    // Update theme if needed
    if (site && site.qmkey_product) {
      state.currentTheme = site.qmkey_product.toLowerCase();
    }
  },

  SET_CENTROS(state, centros_list) {
    function pathify(c) {
      if (c.parent && centro_map[c.parent] !== undefined)
        return pathify(centro_map[c.parent]).concat([c.id]);
      return [c.id];
    }

    const centro_map = {};
    const site_centros = {};
    for (const centro of centros_list) {
      centro_map[centro.id] = centro;
      if (site_centros[centro.site] === undefined)
        site_centros[centro.site] = [];
      site_centros[centro.site].push(centro);
    }

    for (const centro of Object.values(centro_map)) {
      centro.path = pathify(centro);
      centro.path_str = '/' + centro.path.map(c => centro_map[c].name || "???").join('/');
    }

    state.centros = centro_map;
    state.site_centros = site_centros;
  },

  UPDATE_CENTRO(state, centro) {
    state.centros[centro.id] = state.centros[centro.id] || {};
    for (const [key, value] of Object.entries(centro)) {
      state.centros[centro.id][key] = value;
    }
  },

  SET_CURRENT_THEME(state, theme) {
    state.currentTheme = theme;
  },

  SET_CURRENT_PROCESS(state, process_id) {  // Deprecated
    state.processID = process_id;
  },

  SET_PROCESSES(state, processes) {
    state.allProcesses = processes;
    const procmap = {};
    for (const proc of processes) {
      procmap[''+proc.id] = proc;
    }
    state.processes = procmap;
    state.isLoading = false;
  },

  SET_MENU_ITEMS(state, items) {
    state.menus = items;
    state.isLoading = false;
  },

  SET_VOCABULARY_ITEMS(state, items) {
    state.vocabularies = items;
    state.isLoading = false;
  },

  SET_DIRECTORY_ITEMS(state, items) {
    state.qdirectory = {};
    state.qdirectory_list = {};
    state.qdirectory_centro = {};
    state.qdirectory_centro_list = {};

    function process_items(items, path) {
      for (let item of items) {
        item.path = path.concat(item.id);
        state.qdirectory[item.id] = item;
        if (item.children)
          process_items(item.children, item.path);
      }
    }
    process_items(items, []);

    for (const qdir of Object.values(state.qdirectory)) {
      if (! state.qdirectory_centro[qdir.centro_id])
        state.qdirectory_centro[qdir.centro_id] = {};
      state.qdirectory_centro[qdir.centro_id][qdir.id] = qdir;
    }

    for (const qdir of items) {
      if (state.qdirectory_centro_list[qdir.centro_id] === undefined) {
        state.qdirectory_centro_list[qdir.centro_id] = [];
      }
      state.qdirectory_centro_list[qdir.centro_id].push(qdir);
    }
  },

  SET_LOADING(state, value) {
    state.isLoading = value;
  },

  SET_MESSAGE(state, value) {
    state.alert_message = value;
  },

  SET_CURRENT_UNIT(state, unit) {
    // var fields = {}
    // var fields_list = []
    // for (var i = 0; i < unit['workflow'].length; i++) {
    //   var transaction = unit['workflow'][i]
    //   for (var j = 0; j < transaction['fields'].length; j++) {
    //     var field_id = transaction['fields'][j]['id']
    //     fields[field_id] = transaction['fields'][j]
    //     if (!fields_list.includes(field_id)) fields_list.push(field_id)
    //   }
    // }

    // var aggregatted_fields = []
    // for (var i = 0; i < fields_list.length; i++) {
    //   aggregatted_fields.push(fields[fields_list[i]])
    // }
    // unit['fields'] = aggregatted_fields

    state.current_unit = unit
  },

  SET_UNITS_ITEMS_PER_PAGE(state, items_per_page) {
    state.unitsItemsPerPage = items_per_page
  },

  SET_PARENT_PROCESS(state, bp) {
    state.current_parent_process = bp
  },

  SET_UNITS(state, units) {
    // Deprecatetd
    state.units = units
    if (units && !state.unitsItemsPerPage)
      state.unitsItemsPerPage = 10
  },

  SET_QUNITS(state, qunits) {
    // Deprecatetd
    state.qunits = qunits
    if (qunits && !state.unitsItemsPerPage)
      state.unitsItemsPerPage = 10
  },

  SET_CALENDAR_UNITS_DATA(state, data) {
    state.calendar_units = data
  },

  SET_QUNITS_DATA(state, qunits) {
    state.qunits = qunits;
  },

  RESET_QUNITS_DATA(state) {
    state.qunits = new QUnitCollection({});
  },

  RESET_CALENDAR_UNITS_DATA(state) {
    state.calendar_units = []
  },

  SET_USER(state, user) {
    state.isLoading = false
  },

  SET_USERS(state, users) {
    state.users = users;
    state.users_map = {};
    for (const usr of users) {
      state.users_map[usr.id] = usr;
    }
    state.isLoading = false;
  },

  // Search and filter data
  SET_SEARCH_TEXT(state, searchText) {
    state.searchText = searchText
  },
  SET_FILTER_FORM(state, filterFormData) {
    state.filterFormData = filterFormData
  },
  SET_FILTER_DATES(state, filterDates) {
    state.filterDates = filterDates
  },
  SET_ACTIVE_DIRECTORY(state, directory) {
    state.activeDirectory = directory;
  },
  SET_FILTER_PROCESSES(state, processes_ids) {
    state.filterProcesses = processes_ids;
  },

  RESET_EXPLORER_FILTERS(state, keep_process) {
    state.searchText = ''
    state.filterFormData = []
    state.filterDates = []
    state.activeDirectory = {}
    // state.filterProcesses = []
    if (!keep_process) {
      state.processID = "";
      state.filterProcesses = []
    }
  },

  SET_CONTEXT_TITLE(state, context_title) {
    state.context_title = context_title
  },

  SET_CURRENT_USER_NOTIFICATIONS(state, notifications) {
    state.notifications = notifications;
  },

  SET_CURRENT_USER_NOTIFICATIONS_COUNT(state, notifications_count) {
    state.notifications_count = notifications_count;
  },
  SET_IS_MOBILE(state, is_moblie){
    state.is_mobile = is_moblie;
  },
  SET_DISABLED_COMPONENT(state, process_to_wait){
    state.disabled_components = {...state.disabled_components, ...process_to_wait};
  },
}


export default new Vuex.Store({
  modules: {
    QMenu: QMenuStore,
    QVocabulary: QVocabularyStore,
    UserSession: UserSessionStore,
  },
  state,
  getters,
  actions,
  mutations,
});
